import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Collapse, Drawer, Input, Select, Slider, Space, Statistic, Table, Tabs, Tag, Typography, message } from 'antd';
import { FilterOutlined, LoadingOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import AhriFeaturedManagement from './AhriFeaturedManagement';
import AhriInventoryRecompute from './AhriInventoryRecompute';
import AssignAhriModelModal from '../components/AhriDashboard/assignModels/AssignAhriModelModal';

const MATCH_STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Complete', value: 'complete' },
  { label: 'Partial', value: 'partial' },
  { label: 'None', value: 'none' },
];

const FEATURED_STATUS_OPTIONS = [
  { label: 'All Group States', value: 'all' },
  { label: 'In Featured Group', value: 'featured' },
  { label: 'Not In Featured Group', value: 'not_featured' },
];

const SORT_MAP = {
  ahri_id: 'ahri_id',
  program_name: 'program_name',
  indoor_brand: 'indoor_brand',
  outdoor_brand: 'outdoor_brand',
  indoor_model: 'indoor_model',
  outdoor_model: 'outdoor_model',
  match_status: 'match_status',
  featured: 'featured',
  featured_at: 'featured_at',
  updated_at: 'updated_at',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const getFeaturedGroupStatus = (group) => {
  const now = new Date();
  const start = group?.start_at ? new Date(group.start_at) : null;
  const end = group?.end_at ? new Date(group.end_at) : null;
  const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  if (!end) {
    if (start && start > now) return { label: `Scheduled — starts ${fmt(start)}`, color: 'blue' };
    return { label: 'Never expires', color: 'green' };
  }
  if (end < now) return { label: `Expired ${fmt(end)}`, color: 'default' };
  if (start && start > now) return { label: `Scheduled — starts ${fmt(start)}`, color: 'blue' };
  return { label: `Active — expires ${fmt(end)}`, color: 'success' };
};

const JSON_VALUE_COLORS = {
  string: '#0b7285',
  number: '#9c36b5',
  boolean: '#e8590c',
  null: '#868e96',
};

const JsonPrimitive = ({ value }) => {
  if (value === null || value === undefined) {
    return <Typography.Text code style={{ color: JSON_VALUE_COLORS.null }}>null</Typography.Text>;
  }
  const type = typeof value;
  if (type === 'string') {
    return <Typography.Text code style={{ color: JSON_VALUE_COLORS.string }}>"{value}"</Typography.Text>;
  }
  if (type === 'number' || type === 'boolean') {
    return <Typography.Text code style={{ color: JSON_VALUE_COLORS[type] }}>{String(value)}</Typography.Text>;
  }
  return <Typography.Text code>{String(value)}</Typography.Text>;
};

const JsonTree = ({ data, depth = 0 }) => {
  if (data === null || typeof data !== 'object') {
    return <JsonPrimitive value={data} />;
  }
  const isArray = Array.isArray(data);
  const entries = isArray ? data.map((v, i) => [i, v]) : Object.entries(data);
  if (!entries.length) {
    return <Typography.Text type="secondary">{isArray ? '[ ]' : '{ }'}</Typography.Text>;
  }
  return (
    <div style={{ borderLeft: depth > 0 ? '2px solid #f0f0f0' : 'none', paddingLeft: depth > 0 ? 12 : 0 }}>
      {entries.map(([key, value]) => {
        const isNested = value !== null && typeof value === 'object' && Object.keys(value).length > 0;
        return (
          <div key={key} className="py-1" style={{ borderBottom: '1px dashed #f5f5f5' }}>
            {isNested ? (
              <Collapse
                size="small"
                bordered={false}
                defaultActiveKey={depth < 1 ? ['panel'] : []}
                items={[
                  {
                    key: 'panel',
                    label: (
                      <Space size={6}>
                        <Typography.Text strong>{key}</Typography.Text>
                        <Tag color={Array.isArray(value) ? 'purple' : 'blue'}>
                          {Array.isArray(value) ? `array · ${value.length}` : `object · ${Object.keys(value).length}`}
                        </Tag>
                      </Space>
                    ),
                    children: <JsonTree data={value} depth={depth + 1} />,
                  },
                ]}
              />
            ) : (
              <Space align="start" size={8}>
                <Typography.Text strong>{key}:</Typography.Text>
                <JsonTree data={value} depth={depth + 1} />
              </Space>
            )}
          </div>
        );
      })}
    </div>
  );
};

const AhriSystemsReview = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('systems');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    programs: [],
    brands: [],
    match_status: MATCH_STATUS_OPTIONS.map((o) => o.value),
    featured_status: FEATURED_STATUS_OPTIONS.map((o) => o.value),
    featured_groups: [],
  });
  const [query, setQuery] = useState({
    search: '',
    program: '',
    brand: '',
    match_status: 'all',
    featured_status: 'all',
    featured_group_key: '',
    featured_valid_at: '',
    page: 1,
    page_size: 25,
    sort_field: 'updated_at',
    sort_order: 'desc',
  });
  const [draftSearch, setDraftSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, total_pages: 1 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantsFallback, setVariantsFallback] = useState(null);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [drawerWidthPct, setDrawerWidthPct] = useState(50);
  const [ahriFeaturedGroups, setAhriFeaturedGroups] = useState([]);
  const [ahriFeaturedGroupsLoading, setAhriFeaturedGroupsLoading] = useState(false);
  const [allFeaturedGroups, setAllFeaturedGroups] = useState([]);
  const [groupToAdd, setGroupToAdd] = useState(undefined);
  const [addingToGroup, setAddingToGroup] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [facetsLoading, setFacetsLoading] = useState(false);
  const [activeFacetLoading, setActiveFacetLoading] = useState('');
  const [filterDraft, setFilterDraft] = useState({
    search: '',
    program: '',
    brand: '',
    match_status: 'all',
    featured_status: 'all',
    featured_group_key: '',
    featured_valid_at: '',
  });

  const loadData = useCallback(
    async (nextQuery = query) => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams();
        Object.entries(nextQuery).forEach(([key, value]) => {
          const normalized = value == null ? '' : String(value).trim();
          if (normalized !== '') params.set(key, normalized);
        });
        const response = await fetch(`${API_BASE_URL}/api/v1/ahri/systems/review?${params.toString()}`, {
          headers: getAuthHeaders(),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(String(data?.detail || response.statusText || 'Failed to load AHRI systems'));
        setRows(Array.isArray(data?.items) ? data.items : []);
        setPagination({
          page: Number(data?.pagination?.page || nextQuery.page || 1),
          page_size: Number(data?.pagination?.page_size || nextQuery.page_size || 25),
          total: Number(data?.pagination?.total || 0),
          total_pages: Number(data?.pagination?.total_pages || 1),
        });
      } catch (err) {
        setRows([]);
        setError(String(err?.message || err));
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders, query]
  );

  useEffect(() => {
    if (activeTab === 'systems') loadData(query);
  }, [activeTab, loadData, query]);

  const loadVariants = useCallback(
    async (ahriId) => {
      if (!ahriId) return;
      setVariantsLoading(true);
      setVariants([]);
      setVariantsFallback(null);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/ahri/systems/${encodeURIComponent(ahriId)}/variants`,
          { headers: getAuthHeaders() }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(String(data?.detail || response.statusText || 'Failed to load combinations'));
        setVariants(Array.isArray(data?.variants) ? data.variants : []);
        setVariantsFallback(data?.fallback || null);
      } catch (err) {
        message.error(String(err?.message || err));
      } finally {
        setVariantsLoading(false);
      }
    },
    [getAuthHeaders]
  );

  useEffect(() => {
    if (detailOpen && selectedRow?.ahri_id) loadVariants(selectedRow.ahri_id);
  }, [detailOpen, selectedRow?.ahri_id, loadVariants]);

  // Opens the AHRI reference id on ahridirectory.org's public "details" page. The
  // details URL is keyed by AHRI's internal ProgramId, which isn't derivable
  // client-side, so this calls our backend proxy (GET .../systems/{id}/directory-url)
  // to resolve it. The tab is opened synchronously (before the fetch) and only
  // navigated once the URL resolves, since most browsers block a window.open()
  // call made after an intervening await inside a click handler.
  const openAhriDirectoryLink = useCallback(
    (ahriId, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      const normalizedId = String(ahriId || '').trim();
      if (!normalizedId) return;
      const newTab = window.open('', '_blank', 'noopener,noreferrer');
      (async () => {
        try {
          const resp = await fetch(
            `${API_BASE_URL}/api/v1/ahri/systems/${encodeURIComponent(normalizedId)}/directory-url`,
            { headers: getAuthHeaders() }
          );
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok || !data?.url) {
            if (newTab && !newTab.closed) newTab.close();
            return;
          }
          if (newTab && !newTab.closed) {
            newTab.location.href = data.url;
          } else {
            window.open(data.url, '_blank', 'noopener,noreferrer');
          }
        } catch (err) {
          if (newTab && !newTab.closed) newTab.close();
          // eslint-disable-next-line no-console
          console.warn('AHRI Directory link error:', err);
        }
      })();
    },
    [getAuthHeaders]
  );

  // Renders "12345" as a clickable id that opens the AHRI Directory details page
  // for it in a new tab (see openAhriDirectoryLink above). Falls back to plain
  // "-" text when there's no id to link.
  const renderAhriIdText = useCallback(
    (ahriId) => {
      const idText = ahriId || '-';
      if (!ahriId) return idText;
      return (
        <span
          role="button"
          tabIndex={0}
          onClick={(event) => openAhriDirectoryLink(ahriId, event)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              openAhriDirectoryLink(ahriId, event);
            }
          }}
          title="Open on AHRI Directory"
          style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
        >
          {idText}
        </span>
      );
    },
    [openAhriDirectoryLink]
  );

  const loadAhriFeaturedGroups = useCallback(
    async (ahriId) => {
      if (!ahriId) return;
      setAhriFeaturedGroupsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/ahri/systems/${encodeURIComponent(ahriId)}/featured-groups`,
          { headers: getAuthHeaders() }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(String(data?.detail || response.statusText || 'Failed to load featured groups'));
        setAhriFeaturedGroups(Array.isArray(data?.groups) ? data.groups : []);
      } catch (err) {
        message.error(String(err?.message || err));
        setAhriFeaturedGroups([]);
      } finally {
        setAhriFeaturedGroupsLoading(false);
      }
    },
    [getAuthHeaders]
  );

  const loadAllFeaturedGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/ahri/featured-groups`, { headers: getAuthHeaders() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(String(data?.detail || response.statusText || 'Failed to load groups'));
      setAllFeaturedGroups(Array.isArray(data?.groups) ? data.groups : []);
    } catch (err) {
      message.error(String(err?.message || err));
      setAllFeaturedGroups([]);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (detailOpen && selectedRow?.ahri_id) {
      loadAhriFeaturedGroups(selectedRow.ahri_id);
      loadAllFeaturedGroups();
      setGroupToAdd(undefined);
    }
  }, [detailOpen, selectedRow?.ahri_id, loadAhriFeaturedGroups, loadAllFeaturedGroups]);

  const addAhriToFeaturedGroup = useCallback(async () => {
    if (!selectedRow?.ahri_id || !groupToAdd) return;
    setAddingToGroup(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/ahri/featured-groups/${groupToAdd}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ members: [{ ahri_id: selectedRow.ahri_id }] }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(String(data?.detail || response.statusText || 'Failed to add to group'));
      message.success('Added to featured group');
      setGroupToAdd(undefined);
      await loadAhriFeaturedGroups(selectedRow.ahri_id);
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setAddingToGroup(false);
    }
  }, [selectedRow?.ahri_id, groupToAdd, getAuthHeaders, loadAhriFeaturedGroups]);

  const removeAhriFromFeaturedGroup = useCallback(
    async (groupId) => {
      if (!selectedRow?.ahri_id || !groupId) return;
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/ahri/featured-groups/${groupId}/members/${encodeURIComponent(selectedRow.ahri_id)}`,
          { method: 'DELETE', headers: getAuthHeaders() }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(String(data?.detail || response.statusText || 'Failed to remove from group'));
        message.success('Removed from featured group');
        await loadAhriFeaturedGroups(selectedRow.ahri_id);
      } catch (err) {
        message.error(String(err?.message || err));
      }
    },
    [selectedRow?.ahri_id, getAuthHeaders, loadAhriFeaturedGroups]
  );

  const tableData = useMemo(() => rows.map((row) => ({ ...row, key: row.id })), [rows]);
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (query.search) count += 1;
    if (query.program) count += 1;
    if (query.brand) count += 1;
    if (query.match_status !== 'all') count += 1;
    if (query.featured_status !== 'all') count += 1;
    if (query.featured_group_key) count += 1;
    if (query.featured_valid_at) count += 1;
    return count;
  }, [query]);

  const loadFacetValues = useCallback(async (facetKey = '') => {
    setFacetsLoading(true);
    setActiveFacetLoading(facetKey || '');
    try {
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('page_size', '1');
      params.set('sort_field', query.sort_field || 'updated_at');
      params.set('sort_order', query.sort_order || 'desc');
      params.set('include_facets', 'true');
      const response = await fetch(`${API_BASE_URL}/api/v1/ahri/systems/review?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(String(data?.detail || 'Unable to load filter facets'));
      setFilters({
        programs: Array.isArray(data?.filters?.programs) ? data.filters.programs : [],
        brands: Array.isArray(data?.filters?.brands) ? data.filters.brands : [],
        match_status: Array.isArray(data?.filters?.match_status) ? data.filters.match_status : MATCH_STATUS_OPTIONS.map((o) => o.value),
        featured_status: Array.isArray(data?.filters?.featured_status) ? data.filters.featured_status : FEATURED_STATUS_OPTIONS.map((o) => o.value),
        featured_groups: Array.isArray(data?.filters?.featured_groups) ? data.filters.featured_groups : [],
      });
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setFacetsLoading(false);
      setActiveFacetLoading('');
    }
  }, [getAuthHeaders, query.sort_field, query.sort_order]);

  const openFilterDrawer = useCallback(() => {
    setFilterDraft({
      search: draftSearch || query.search || '',
      program: query.program || '',
      brand: query.brand || '',
      match_status: query.match_status || 'all',
      featured_status: query.featured_status || 'all',
      featured_group_key: query.featured_group_key || '',
      featured_valid_at: query.featured_valid_at || '',
    });
    setFilterDrawerOpen(true);
    loadFacetValues();
  }, [draftSearch, loadFacetValues, query]);

  const appliedFilterTags = useMemo(() => {
    const tags = [];
    if (query.search) tags.push({ key: 'search', label: `Search: ${query.search}` });
    if (query.program) tags.push({ key: 'program', label: `Program: ${query.program}` });
    if (query.brand) tags.push({ key: 'brand', label: `Brand: ${query.brand}` });
    if (query.match_status !== 'all') tags.push({ key: 'match_status', label: `Match: ${query.match_status}` });
    if (query.featured_status !== 'all') tags.push({ key: 'featured_status', label: `Featured: ${query.featured_status}` });
    if (query.featured_group_key) {
      const group = (filters.featured_groups || []).find((entry) => String(entry?.group_key || '') === String(query.featured_group_key));
      tags.push({ key: 'featured_group_key', label: `Group: ${group?.name || query.featured_group_key}` });
    }
    if (query.featured_valid_at) tags.push({ key: 'featured_valid_at', label: `Valid At: ${query.featured_valid_at}` });
    return tags;
  }, [filters.featured_groups, query]);

  const applyFilterDrawer = useCallback(() => {
    const nextSearch = String(filterDraft.search || '').trim();
    setDraftSearch(nextSearch);
    setQuery((prev) => ({
      ...prev,
      search: nextSearch,
      program: filterDraft.program || '',
      brand: filterDraft.brand || '',
      match_status: filterDraft.match_status || 'all',
      featured_status: filterDraft.featured_status || 'all',
      featured_group_key: filterDraft.featured_group_key || '',
      featured_valid_at: filterDraft.featured_valid_at || '',
      page: 1,
    }));
    setFilterDrawerOpen(false);
  }, [filterDraft]);

  const clearAllFilters = useCallback(() => {
    setDraftSearch('');
    setFilterDraft({
      search: '',
      program: '',
      brand: '',
      match_status: 'all',
      featured_status: 'all',
      featured_group_key: '',
      featured_valid_at: '',
    });
    setQuery((prev) => ({
      ...prev,
      search: '',
      program: '',
      brand: '',
      match_status: 'all',
      featured_status: 'all',
      featured_group_key: '',
      featured_valid_at: '',
      page: 1,
    }));
    setFilterDrawerOpen(false);
  }, []);

  const tableColumns = useMemo(
    () => [
      { title: 'AHRI ID', dataIndex: 'ahri_id', key: 'ahri_id', width: 170, sorter: true },
      { title: 'Program', dataIndex: 'program_name', key: 'program_name', sorter: true, ellipsis: true },
      {
        title: 'Indoor',
        key: 'indoor',
        width: 260,
        render: (_, record) => (
          <div>
            <div className="fw-semibold">{record.indoor_brand || '-'}</div>
            <div className="text-muted small">{record.indoor_model || '-'}</div>
          </div>
        ),
      },
      {
        title: 'Outdoor',
        key: 'outdoor',
        width: 260,
        render: (_, record) => (
          <div>
            <div className="fw-semibold">{record.outdoor_brand || '-'}</div>
            <div className="text-muted small">{record.outdoor_model || '-'}</div>
          </div>
        ),
      },
      {
        title: 'Match',
        dataIndex: 'ahri_complete_match',
        key: 'match_status',
        width: 120,
        sorter: true,
        render: (value) => (value ? <Tag color="success">Complete</Tag> : <Tag color="default">Needs review</Tag>),
      },
      {
        title: 'Featured',
        dataIndex: 'featured',
        key: 'featured',
        width: 160,
        sorter: true,
        render: (value) => (value ? <Tag color="success">Featured Group</Tag> : <Tag>Not Featured</Tag>),
      },
      { title: 'Featured At', dataIndex: 'featured_at', key: 'featured_at', width: 190, sorter: true, render: (value) => <span className="small">{formatDateTime(value)}</span> },
      { title: 'Updated', dataIndex: 'updated_at', key: 'updated_at', width: 190, sorter: true, render: (value) => <span className="small">{formatDateTime(value)}</span> },
      {
        title: 'Action',
        key: 'action',
        width: 120,
        fixed: 'right',
        render: (_, record) => (
          <Button size="small" onClick={() => { setSelectedRow(record); setDetailOpen(true); }}>
            Review
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div className="container-fluid py-3 py-md-4">
      <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2 mb-3">
        <div>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            AHRI Systems Review
          </Typography.Title>
          <Typography.Text type="secondary">
            AHRI systems review and featured management.
          </Typography.Text>
        </div>
        {activeTab === 'systems' && (
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => loadData(query)} loading={loading}>
              Refresh
            </Button>
          </Space>
        )}
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'systems', label: 'AHRI Systems' },
          { key: 'featured', label: 'Featured AHRIs' },
          { key: 'inventory', label: 'Inventory' },
        ]}
      />

      {activeTab === 'featured' ? (
        <AhriFeaturedManagement />
      ) : activeTab === 'inventory' ? (
        <AhriInventoryRecompute />
      ) : (
        <>
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-4">
              <Card size="small" variant="borderless">
                <Statistic title="Total Records" value={pagination.total} />
              </Card>
            </div>
            <div className="col-12 col-md-4">
              <Card size="small" variant="borderless">
                <Statistic title="Current Page" value={`${pagination.page} / ${pagination.total_pages}`} />
              </Card>
            </div>
            <div className="col-12 col-md-4">
              <Card size="small" variant="borderless">
                <Statistic title="Page Size" value={pagination.page_size} />
              </Card>
            </div>
          </div>

          <Card variant="borderless" styles={{ body: { paddingBottom: 8 } }} className="mb-3">
            <div className="d-flex flex-column flex-lg-row gap-2 align-items-stretch align-items-lg-center">
              <div className="flex-grow-1">
                <Input
                  placeholder="Search AHRI ID, brands, model numbers, program"
                  value={draftSearch}
                  allowClear
                  prefix={<SearchOutlined />}
                  onChange={(e) => setDraftSearch(e.target.value)}
                  onPressEnter={() => setQuery((prev) => ({ ...prev, search: draftSearch.trim(), page: 1 }))}
                />
              </div>
              <div className="d-flex gap-2">
                <Button icon={<FilterOutlined />} onClick={openFilterDrawer} loading={facetsLoading}>
                  Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
                </Button>
                <Button onClick={clearAllFilters} disabled={activeFilterCount === 0}>
                  Clear Filters
                </Button>
                <Button type="primary" onClick={() => setQuery((prev) => ({ ...prev, search: draftSearch.trim(), page: 1 }))}>
                  Search
                </Button>
              </div>
            </div>
            {appliedFilterTags.length > 0 ? (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilterTags.map((entry) => (
                  <Tag key={entry.key} color="blue">
                    {entry.label}
                  </Tag>
                ))}
              </div>
            ) : null}
          </Card>

          {error && <Alert className="mb-3" type="error" showIcon message={error} />}

          <Card variant="borderless">
            <Table
              rowKey="id"
              loading={loading}
              columns={tableColumns}
              dataSource={tableData}
              scroll={{ x: 1200 }}
              pagination={{
                current: pagination.page,
                pageSize: pagination.page_size,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
              }}
              onChange={(nextPagination, _tableFilters, sorter) => {
                const nextSortField = SORT_MAP[sorter?.field] || query.sort_field;
                const nextSortOrder = sorter?.order === 'ascend' ? 'asc' : sorter?.order === 'descend' ? 'desc' : query.sort_order;
                setQuery((prev) => ({
                  ...prev,
                  page: nextPagination.current || 1,
                  page_size: nextPagination.pageSize || prev.page_size,
                  sort_field: nextSortField,
                  sort_order: nextSortOrder,
                }));
              }}
            />
          </Card>

          <Drawer
            title="Filter AHRI Systems"
            placement="right"
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            width={380}
          >
            <div className="d-flex flex-column gap-3">
              <div>
                <div className="form-label mb-1">Search</div>
                <Input
                  placeholder="Search text"
                  value={filterDraft.search}
                  allowClear
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, search: e.target.value }))}
                />
              </div>
              <div>
                <div className="form-label mb-1">Program</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Any program"
                  loading={facetsLoading && activeFacetLoading === 'program'}
                  suffixIcon={facetsLoading && activeFacetLoading === 'program' ? <LoadingOutlined spin /> : undefined}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={filterDraft.program || undefined}
                  options={filters.programs.map((value) => ({ label: value, value }))}
                  onChange={(value) => setFilterDraft((prev) => ({ ...prev, program: value || '' }))}
                  onOpenChange={(open) => {
                    if (open) loadFacetValues('program');
                  }}
                />
              </div>
              <div>
                <div className="form-label mb-1">Brand</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Any brand"
                  loading={facetsLoading && activeFacetLoading === 'brand'}
                  suffixIcon={facetsLoading && activeFacetLoading === 'brand' ? <LoadingOutlined spin /> : undefined}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={filterDraft.brand || undefined}
                  options={filters.brands.map((value) => ({ label: value, value }))}
                  onChange={(value) => setFilterDraft((prev) => ({ ...prev, brand: value || '' }))}
                  onOpenChange={(open) => {
                    if (open) loadFacetValues('brand');
                  }}
                />
              </div>
              <div>
                <div className="form-label mb-1">Match Status</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Match Status"
                  value={filterDraft.match_status}
                  options={MATCH_STATUS_OPTIONS.filter((option) => (filters.match_status || []).includes(option.value))}
                  onChange={(value) => setFilterDraft((prev) => ({ ...prev, match_status: value || 'all' }))}
                />
              </div>
              <div>
                <div className="form-label mb-1">Featured Group Status</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Featured Group Status"
                  value={filterDraft.featured_status}
                  options={FEATURED_STATUS_OPTIONS.filter((option) => (filters.featured_status || []).includes(option.value))}
                  onChange={(value) => setFilterDraft((prev) => ({ ...prev, featured_status: value || 'all' }))}
                />
              </div>
              <div>
                <div className="form-label mb-1">Featured Group</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Any featured group"
                  loading={facetsLoading && activeFacetLoading === 'featured_group'}
                  suffixIcon={facetsLoading && activeFacetLoading === 'featured_group' ? <LoadingOutlined spin /> : undefined}
                  allowClear
                  value={filterDraft.featured_group_key || undefined}
                  options={(filters.featured_groups || []).map((entry) => ({
                    label: entry?.name || entry?.group_key,
                    value: entry?.group_key,
                  }))}
                  onChange={(value) => setFilterDraft((prev) => ({ ...prev, featured_group_key: value || '' }))}
                  onOpenChange={(open) => {
                    if (open) loadFacetValues('featured_group');
                  }}
                />
              </div>
              <div>
                <div className="form-label mb-1">Featured Valid At</div>
                <Input
                  style={{ width: '100%' }}
                  type="datetime-local"
                  value={filterDraft.featured_valid_at || ''}
                  onChange={(event) => setFilterDraft((prev) => ({ ...prev, featured_valid_at: event.target.value || '' }))}
                />
              </div>
              <div className="d-flex gap-2">
                <Button onClick={clearAllFilters}>Clear</Button>
                <Button type="primary" onClick={applyFilterDrawer}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </Drawer>

          <Drawer
            title={selectedRow ? <>System {renderAhriIdText(selectedRow.ahri_id)}</> : 'System details'}
            placement="right"
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            width={`${drawerWidthPct}vw`}
            extra={(
              <div style={{ width: 180 }}>
                <div className="small text-muted mb-1">Width ({drawerWidthPct}%)</div>
                <Slider min={50} max={75} value={drawerWidthPct} onChange={setDrawerWidthPct} tooltip={{ formatter: (value) => `${value}%` }} />
              </div>
            )}
          >
            {!selectedRow ? (
              <Typography.Text type="secondary">No row selected.</Typography.Text>
            ) : (
              <div className="d-flex flex-column gap-3">
                <Card size="small">
                  <div className="row g-2">
                    <div className="col-12 col-md-6"><strong>AHRI ID:</strong> {renderAhriIdText(selectedRow.ahri_id)}</div>
                    <div className="col-12 col-md-6"><strong>Program:</strong> {selectedRow.program_name || '-'}</div>
                    <div className="col-12 col-md-6"><strong>Indoor:</strong> {selectedRow.indoor_brand || '-'} / {selectedRow.indoor_model || '-'}</div>
                    <div className="col-12 col-md-6"><strong>Outdoor:</strong> {selectedRow.outdoor_brand || '-'} / {selectedRow.outdoor_model || '-'}</div>
                    <div className="col-12 col-md-6"><strong>Match:</strong> {selectedRow.ahri_complete_match ? 'Complete' : 'Needs review'}</div>
                    <div className="col-12 col-md-6"><strong>Updated:</strong> {formatDateTime(selectedRow.updated_at)}</div>
                  </div>
                  <div className="mt-2">
                    <Button type="primary" onClick={() => setAssignModalOpen(true)}>
                      Assign Models
                    </Button>
                  </div>
                </Card>
                <Card size="small" title="Featured Groups" loading={ahriFeaturedGroupsLoading}>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {ahriFeaturedGroups.length ? (
                      ahriFeaturedGroups.map((group) => {
                        const status = getFeaturedGroupStatus(group);
                        return (
                          <Tag
                            key={group.id}
                            color={status.color}
                            closable
                            onClose={(event) => {
                              event.preventDefault();
                              removeAhriFromFeaturedGroup(group.id);
                            }}
                          >
                            {group.name || group.group_key} · {status.label}
                          </Tag>
                        );
                      })
                    ) : (
                      <Typography.Text type="secondary">Not in any featured group.</Typography.Text>
                    )}
                  </div>
                  <Space.Compact style={{ width: '100%' }}>
                    <Select
                      showSearch
                      style={{ width: '100%' }}
                      placeholder="Add to a featured group"
                      value={groupToAdd}
                      filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
                      options={(allFeaturedGroups || [])
                        .filter((group) => !ahriFeaturedGroups.some((existing) => String(existing.id) === String(group.id)))
                        .map((group) => ({ label: `${group.name || group.group_key} (${group.member_count ?? 0})`, value: group.id }))}
                      onChange={(value) => setGroupToAdd(value)}
                    />
                    <Button type="primary" loading={addingToGroup} disabled={!groupToAdd} onClick={addAhriToFeaturedGroup}>
                      Add
                    </Button>
                  </Space.Compact>
                </Card>
                <Card size="small" title="Product Combinations" loading={variantsLoading}>
                  {variantsFallback || (variants.length === 1 && variants[0].source === 'ahri_certificate_fallback') ? (
                    <Alert
                      type="warning"
                      showIcon
                      message="No approved product match yet -- showing certificate data"
                      description={`Indoor: ${(variantsFallback || variants[0]).indoor_brand || '-'} / Outdoor: ${(variantsFallback || variants[0]).outdoor_brand || '-'} (unmatched/fallback, not from a real product match)`}
                    />
                  ) : (
                    <Table
                      size="small"
                      rowKey={(v) => `${v.indoor_product_code}_${v.outdoor_product_code}_${v.furnace_product_code}`}
                      pagination={false}
                      dataSource={variants}
                      columns={[
                        {
                          title: 'Indoor',
                          dataIndex: 'indoor_product_code',
                          render: (_, v) => (
                            <span>
                              {v.indoor_product_code || '-'}<br />
                              <Typography.Text type="secondary">
                                {v.indoor_manufacturer || '-'} / {v.indoor_brand || '-'}
                              </Typography.Text>
                            </span>
                          ),
                        },
                        {
                          title: 'Outdoor',
                          dataIndex: 'outdoor_product_code',
                          render: (_, v) => (
                            <span>
                              {v.outdoor_product_code || '-'}<br />
                              <Typography.Text type="secondary">
                                {v.outdoor_manufacturer || '-'} / {v.outdoor_brand || '-'}
                              </Typography.Text>
                            </span>
                          ),
                        },
                        { title: 'Furnace', dataIndex: 'furnace_product_code', render: (v) => v || '-' },
                        {
                          title: 'Complete',
                          dataIndex: 'is_complete_match',
                          render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'Complete' : 'Partial'}</Tag>,
                        },
                        {
                          title: 'Source',
                          dataIndex: 'source',
                          render: (v) => <Tag>{v || 'auto'}</Tag>,
                        },
                        {
                          title: 'Availability',
                          dataIndex: 'availability_json',
                          render: (v) => {
                            const warehouses = Object.keys(v || {}).filter((wh) => v[wh]);
                            return warehouses.length ? warehouses.join(', ') : <Typography.Text type="secondary">None</Typography.Text>;
                          },
                        },
                      ]}
                    />
                  )}
                </Card>
                <Card size="small" title="Raw Row JSON">
                  <JsonTree data={selectedRow.row_json || {}} />
                </Card>
              </div>
            )}
          </Drawer>

          <AssignAhriModelModal
            open={assignModalOpen}
            onClose={() => setAssignModalOpen(false)}
            ahriId={selectedRow?.ahri_id}
            rowJson={selectedRow?.row_json}
            initialCompleteMatch={selectedRow?.ahri_complete_match}
            getAuthHeaders={getAuthHeaders}
            onStatusChange={(next) => {
              setSelectedRow((prev) => (prev ? { ...prev, ahri_complete_match: next.complete } : prev));
              setRows((prev) =>
                prev.map((r) => (r.ahri_id === selectedRow?.ahri_id ? { ...r, ahri_complete_match: next.complete } : r))
              );
            }}
          />
        </>
      )}
    </div>
  );
};

export default AhriSystemsReview;
